/**
 * RoadmapTab — the panel's "Roadmap" tab content.
 * SPEC-stakeholder-portal-presentation-redesign-2026-07-20, R4 AC1/AC2/AC4/AC5.
 *
 * R3 shipped this tab as a re-homed copy of the OLD coordinator-styled
 * RoadmapBoard (StatusPill + plain ticket-title rows) so the tab existed and
 * was switchable. R4 replaces that content with the DISTINCT "hype"
 * presentation this ring exists to build (AC1): epics only (R1's
 * /epic-roadmap read — `is_epic = true AND parent_epic_id IS NULL`),
 * bucketed Now/Next/Later, date-free, rendered as a headline + a short
 * public description with a plain "now / coming soon / later" framing — NOT
 * the internal claim-ladder/StatusPill vocabulary. Filtering the existing
 * board to epic rows would just produce a smaller ticket list — the exact
 * miss this ring exists to avoid — so this is a genuinely different card
 * layout, not a filtered RoadmapBoard.
 *
 * The epic-roadmap read is gated by the SAME stakeholder_portal capability +
 * portal_visibility floor as the legacy /roadmap (own_only hides the board;
 * roadmap/planned show it) — a super-admin's admin-role bypass clears the
 * same gate, so this ONE query serves both a client's popover and a
 * super-admin's drawer Roadmap tab (AC2).
 *
 * AC4: a super-admin ADDITIONALLY gets a per-epic expand affordance
 * (`isSuperAdmin`, threaded from PanelBody's panel-config read) that drills
 * into R1's super-admin-gated epic-drill-down endpoint and renders the
 * sub-issues with the SAME internal StatusPill/claim-ladder rendering the
 * Coordination/All-Issues tabs use — the hype/no-jargon rule applies to the
 * epic's OWN row only, never to this internal drill-down panel. A plain
 * client never sees the expand control, and the underlying endpoint 403s a
 * non-super-admin server-side regardless.
 *
 * SPEC-keystone-roadmap-page-2026-07-26 R3: adds a prominent "See the full
 * roadmap" link out to the dedicated `/roadmap` page (R2), and restyles the
 * Now/Next/Later groups + per-epic framing pill to the page's teal/amber
 * accent + shipped/building/horizon node grammar (teal check / amber dot /
 * hollow-dashed ring) so the two roadmaps visually rhyme. The underlying
 * epic-driven Now/Next/Later data + drill-down logic above is unchanged —
 * this ring is styling + a link only.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { StatusPill } from '@/components/portal/StatusPill';
import { PanelErrorState } from '@/components/portal/panel/PanelErrorState';
import { usePortalPanel } from '@/lib/portal/PortalPanelContext';
import type { EpicRoadmapItem, CoordinationWorkItem } from '@/lib/schemas';

// R3: the page's pinned teal/amber palette (spec.yaml cross-cutting
// constraint) applied directly as literal arbitrary-value Tailwind classes —
// the app has no existing token for these brand hex values (checked
// tailwind.config.js), and the page itself (roadmap.css) scopes its CSS
// custom properties under `.roadmap-page`, which this panel is not inside.
// NOTE: Tailwind's JIT content scanner needs LITERAL class strings in source
// (not template-literal-interpolated JS constants) to generate the classes,
// so the hex values below are kept as plain string constants for readability
// in inline `style` usage only — the Tailwind classNames spell the hex out
// literally inline (see HORIZON_GROUPS below).
const RM_TEAL = '#0F8B82';
const RM_TEAL_DEEP = '#0A5C57';
const RM_AMBER = '#C67A1E';

export const PANEL_ROADMAP_QUERY_KEY = ['stakeholder-portal', 'panel', 'roadmap'] as const;

export const NO_DATES_COPY =
  "We share what's now and what's next. We don't commit to dates, so we can prioritise honestly.";

// AC1: a plain public-facing framing per horizon — deliberately NOT the
// internal claim-ladder vocabulary (StatusPill/rung labels).
//
// Visual-review finding (2026-07-24 final pass): the group headers and card
// chrome previously carried identical, muted weight regardless of horizon —
// reading as a quiet status digest rather than the "hype" showcase this ring
// exists to build. Each horizon now carries its own accent (left-border +
// header color) so "Available now" visibly leads.
//
// R3: recolored from the old grayscale emerald/blue/slate palette to the
// page's teal/amber grammar, mapping this tab's Now/Next/Later buckets onto
// the page's shipped/building/horizon node treatment — teal for "available
// now" (shipped-like), amber for "coming soon" (building-like), and a muted
// hollow-dashed treatment for "on the horizon". Only the visual styling
// changed; the horizon KEYS ('now'/'next'/'later') and copy driving the
// epic filtering + tests below are untouched.
const HORIZON_GROUPS: Array<{
  key: 'now' | 'next' | 'later';
  label: string;
  framing: string;
  headerClassName: string;
  borderClassName: string;
  cardClassName: string;
  framingClassName: string;
}> = [
  {
    key: 'now',
    label: 'Now',
    framing: 'Available now',
    headerClassName: 'text-[#0A5C57]',
    borderClassName: 'border-[#0F8B82]',
    cardClassName: 'border-[#0F8B82]/25 bg-[#0F8B82]/5',
    framingClassName: 'bg-[#E2EFED] text-[#0A5C57]',
  },
  {
    key: 'next',
    label: 'Next',
    framing: 'Coming soon',
    headerClassName: 'text-[#C67A1E]',
    borderClassName: 'border-[#C67A1E]',
    cardClassName: 'border-[#C67A1E]/25 bg-[#C67A1E]/5',
    framingClassName: 'bg-[#F5E9D7] text-[#C67A1E]',
  },
  {
    key: 'later',
    label: 'Later',
    framing: 'On the horizon',
    headerClassName: 'text-muted-foreground',
    borderClassName: 'border-dashed border-slate-300',
    cardClassName: 'border border-dashed bg-muted/20',
    framingClassName: 'bg-muted text-muted-foreground',
  },
];

/**
 * R3 AC2: a small node echo of the page's status semantics (teal check /
 * amber dot / hollow-dashed ring) placed beside each horizon group's header,
 * so the panel's Now/Next/Later reads as the same visual system as the
 * page's shipped/building/horizon spine nodes — without touching the h3
 * label text itself (kept exact for the existing horizon-label assertions).
 */
function HorizonNode({ variant }: { variant: 'now' | 'next' | 'later' }) {
  if (variant === 'now') {
    return (
      <span
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-white"
        style={{ backgroundColor: RM_TEAL }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} className="h-2.5 w-2.5">
          <path d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  }
  if (variant === 'next') {
    return (
      <span
        className="inline-block h-3 w-3 shrink-0 rounded-full"
        style={{ backgroundColor: RM_AMBER }}
        aria-hidden="true"
      />
    );
  }
  return (
    <span
      className="inline-block h-3 w-3 shrink-0 rounded-full border-2 border-dashed border-slate-400"
      aria-hidden="true"
    />
  );
}

/**
 * R3 AC1: prominent link out to the dedicated `/roadmap` build-track page
 * (R2). Closes the panel on click (usePortalPanel().closePanel) so the
 * client/super-admin popover-or-drawer doesn't linger open behind the new
 * page. Rendered once, at the top of the tab, so it's visible from both the
 * client popover and the super-admin drawer variant (AC4) without scrolling.
 */
function FullRoadmapLink() {
  const { closePanel } = usePortalPanel();
  return (
    <Link
      to="/roadmap"
      onClick={() => closePanel()}
      data-testid="panel-roadmap-full-link"
      className="flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-semibold transition-colors"
      style={{ borderColor: RM_TEAL, backgroundColor: `${RM_TEAL}0D`, color: RM_TEAL_DEEP }}
    >
      See the full roadmap
      <span aria-hidden="true">→</span>
    </Link>
  );
}

interface RoadmapTabProps {
  /** AC4: only a super-admin gets the per-epic drill-down expand control. */
  isSuperAdmin?: boolean;
}

export function RoadmapTab({ isSuperAdmin = false }: RoadmapTabProps) {
  const roadmapQuery = useQuery({
    queryKey: PANEL_ROADMAP_QUERY_KEY,
    queryFn: () => api.stakeholderPortal.epicRoadmap(),
  });

  if (roadmapQuery.isError) {
    return (
      <PanelErrorState
        what="the roadmap"
        onRetry={() => roadmapQuery.refetch()}
        testId="panel-roadmap-error"
      />
    );
  }

  if (roadmapQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8 px-4 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading the roadmap…
      </div>
    );
  }

  const roadmapVisible = roadmapQuery.data?.roadmap_visible ?? false;
  const items: EpicRoadmapItem[] = roadmapQuery.data?.items ?? [];

  if (!roadmapVisible) {
    return (
      <div className="py-8 px-4 text-center text-sm text-muted-foreground" data-testid="panel-roadmap-not-visible">
        The roadmap isn't available for your account yet.
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3" data-testid="panel-roadmap-tab">
      <p className="text-sm text-muted-foreground" data-testid="portal-no-dates-copy">
        {NO_DATES_COPY}
      </p>
      <FullRoadmapLink />
      <HypeRoadmapBoard items={items} isSuperAdmin={isSuperAdmin} />
    </div>
  );
}

/** AC1: the epics-only HYPE board — Now/Next/Later groups of headline cards. */
function HypeRoadmapBoard({ items, isSuperAdmin }: { items: EpicRoadmapItem[]; isSuperAdmin: boolean }) {
  return (
    <div data-testid="portal-roadmap" className="space-y-4">
      {HORIZON_GROUPS.map((g) => {
        const groupItems = items.filter((i) => i.horizon === g.key);
        return (
          <div key={g.key} className={cn('rounded-md border-l-4 bg-background p-3', g.borderClassName)}>
            <div className="mb-2 flex items-center gap-2">
              <HorizonNode variant={g.key} />
              <h3 className={cn('text-base font-bold tracking-tight', g.headerClassName)}>
                {g.label}
              </h3>
            </div>
            {groupItems.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nothing here yet.</p>
            ) : (
              <ul className="space-y-3" data-testid="portal-roadmap-group">
                {groupItems.map((epic) => (
                  <EpicHypeCard
                    key={epic.work_item_id}
                    epic={epic}
                    framing={g.framing}
                    framingClassName={g.framingClassName}
                    cardClassName={g.cardClassName}
                    isSuperAdmin={isSuperAdmin}
                  />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * One epic's HYPE face: a headline treatment + the public description hook +
 * the horizon's plain framing — deliberately NEVER the epic's own
 * `claim_ladder` / a StatusPill (AC1). No sub-issue list, no count, no date.
 * A super-admin gets an additional expand control (AC4); a plain client sees
 * no such affordance at all.
 */
function EpicHypeCard({
  epic,
  framing,
  framingClassName,
  cardClassName,
  isSuperAdmin,
}: {
  epic: EpicRoadmapItem;
  framing: string;
  framingClassName: string;
  cardClassName: string;
  isSuperAdmin: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className={cn('rounded-md border p-3', cardClassName)} data-testid="portal-roadmap-epic">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {/* Bumped from text-base (16px) to text-xl so the headline visibly
              outranks both the group header and the body copy — visual-review
              finding, 2026-07-24 final pass. */}
          <h4 className="text-xl font-bold leading-snug" data-testid="portal-roadmap-epic-headline">
            {epic.title || <span className="text-muted-foreground">(untitled)</span>}
          </h4>
          {epic.description && (
            <p className="mt-1 text-sm text-muted-foreground" data-testid="portal-roadmap-epic-description">
              {epic.description}
            </p>
          )}
          <span
            className={cn(
              'mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold',
              framingClassName,
            )}
            data-testid="portal-roadmap-epic-framing"
          >
            {/* R3 AC2: echoes the page's rm-stat dot — inherits the pill's
                text color via currentColor so it always matches per-horizon. */}
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
            {framing}
          </span>
        </div>
        {isSuperAdmin && (
          <button
            type="button"
            data-testid="portal-roadmap-epic-expand-toggle"
            className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            aria-expanded={expanded}
            onClick={() => setExpanded((cur) => !cur)}
          >
            Sub-issues
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
      {isSuperAdmin && expanded && <EpicSubIssuesPanel epicId={epic.work_item_id} />}
    </li>
  );
}

/**
 * AC4: the super-admin epic drill-down — fetches R1's
 * /api/coordination/epics/{epic_id} and renders each sub-issue with the SAME
 * internal StatusPill/claim-ladder rendering the Coordination/All-Issues
 * tabs use (AC3's "reuse", not a second parallel presentation).
 */
function EpicSubIssuesPanel({ epicId }: { epicId: string }) {
  const drillQuery = useQuery({
    queryKey: ['stakeholder-portal', 'panel', 'roadmap', 'epic-drill-down', epicId],
    queryFn: () => api.coordination.epicDrillDown(epicId),
  });

  if (drillQuery.isLoading) {
    return (
      <div
        className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"
        data-testid="portal-roadmap-drilldown-loading"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading sub-issues…
      </div>
    );
  }

  if (drillQuery.isError) {
    return (
      <p className="mt-2 text-xs text-destructive" data-testid="portal-roadmap-drilldown-error">
        Could not load this epic's sub-issues.
      </p>
    );
  }

  const subIssues: CoordinationWorkItem[] = drillQuery.data?.sub_issues ?? [];

  return (
    <ul className="mt-2 space-y-2 border-t pt-2" data-testid="portal-roadmap-drilldown-list">
      {subIssues.length === 0 ? (
        <li className="text-xs text-muted-foreground" data-testid="portal-roadmap-drilldown-empty">
          No sub-issues linked yet.
        </li>
      ) : (
        subIssues.map((si) => (
          <li
            key={si.id}
            className="flex items-center justify-between gap-2 text-sm"
            data-testid="portal-roadmap-drilldown-item"
          >
            <span className="min-w-0 truncate">{si.title || '(untitled)'}</span>
            {si.claim_ladder && <StatusPill rung={si.claim_ladder} testId="portal-roadmap-drilldown-status" />}
          </li>
        ))
      )}
    </ul>
  );
}
